.header {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-2);
	padding-block-end: var(--spacing-4);
	margin-block-end: var(--spacing-4);
	border-block-end: 1px solid var(--colour-border-subtle);

	/* Back arrow + the shared Breadcrumb ("Epics / EP-n"), sharing a row above the title.
	   The back icon centres against the breadcrumb text. */
	& .breadcrumb {
		display: flex;
		gap: var(--spacing-2);
		align-items: center;
		line-height: 1;
	}

	& .back {
		display: inline-flex;
		flex: none;
		align-items: center;
		justify-content: center;

		/* The icon button is a 2rem tap target with the glyph centred inside it, so the chevron
		   would otherwise sit ~0.75rem in from the button box and the breadcrumb would read as
		   inset. Pull it back by that much so the arrow lines up on the page's left edge with
		   the title, status dot and cards below (the tap target keeps its full size). */
		margin-inline-start: calc(-1 * var(--spacing-3));
		color: var(--colour-text-tertiary);

		/* Sized to the breadcrumb text, so the chevron's bounds match the mono caps beside
		   it (the 1rem icon overshot above and below, reading as misaligned). */
		& svg {
			inline-size: 0.875rem;
			block-size: 0.875rem;
		}
	}

	/* Eyebrow (EP-n + status) on the left, people + menu on the right. */
	& .top {
		display: flex;
		gap: var(--spacing-3);
		align-items: center;
		justify-content: space-between;
	}

	& .eyebrow {
		display: flex;
		gap: var(--spacing-2);
		align-items: center;
	}

	/* Status pill, now an interactive Planned/Active/Done select (the epic's status control,
	   like the board's). Nested under .header so it overrides the shared Select trigger base;
	   keeps the tinted pill look with a faint chevron that firms up on hover/open. */
	& .status {
		display: inline-flex;
		gap: var(--spacing-1);
		align-items: center;
		inline-size: auto;
		min-block-size: 0;
		padding: 0.125rem var(--spacing-2);
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-semibold);
		text-transform: uppercase;
		letter-spacing: var(--letter-spacing-wide);
		cursor: pointer;
		border: none;
		border-radius: var(--radius-full);
		transition: filter var(--motion-fast) var(--ease-standard);

		&:hover {
			filter: brightness(0.97);
		}

		&:focus-visible {
			outline: none;
			box-shadow: 0 0 0 2px var(--colour-brand-primary);
		}

		/* The built-in chevron reads as the edit caret: faint at rest, clearer on hover/open. */
		& svg {
			inline-size: 0.6875rem;
			block-size: 0.6875rem;
			opacity: 0.55;
		}

		&:hover svg,
		&[data-state='open'] svg {
			opacity: 0.9;
		}

		&[data-status='PLANNED'] {
			color: var(--colour-text-secondary);
			background: var(--colour-background-muted);
		}

		&[data-status='ACTIVE'] {
			color: var(--colour-brand-primary);
			background: color-mix(in srgb, var(--colour-brand-primary) 14%, transparent);
		}

		&[data-status='DONE'] {
			color: var(--colour-label-green);
			background: color-mix(in srgb, var(--colour-label-green) 16%, transparent);
		}
	}

	& .archived {
		padding: 0.125rem var(--spacing-2);
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-semibold);
		color: var(--colour-text-tertiary);
		text-transform: uppercase;
		letter-spacing: var(--letter-spacing-wide);
		background: var(--colour-background-muted);
		border-radius: var(--radius-full);
	}

	/* Owner + assignee avatars + the "…" menu. */
	& .people {
		display: flex;
		gap: var(--spacing-3);
		align-items: center;
	}

	/* The owner slot is an inline picker: strip the shared Select trigger back to the chip
	   (no border/box, chevron hidden) so the avatar + name read as the whole control. Scoped
	   under .header so it beats the shared trigger base without !important. */
	& .ownerTrigger {
		inline-size: auto;

		/* Reserve the tallest state's height (the small avatar chip: 2rem avatar +
		   spacing-1 top/bottom padding = 2.5rem) so setting/clearing an owner never
		   moves the header. The base trigger already flex-centres, so the shorter
		   "No owner" text state fills the same box. */
		min-block-size: var(--spacing-7);
		padding: var(--spacing-1) var(--spacing-2);
		cursor: pointer;
		background: none;
		border: 1px solid transparent;
		border-radius: var(--radius-full);
		transition:
			background-color var(--motion-fast) var(--ease-standard),
			border-color var(--motion-fast) var(--ease-standard);

		&:hover {
			background-color: var(--colour-background-muted);
			border-color: transparent;
		}

		&:focus-visible {
			outline: none;
			box-shadow: 0 0 0 2px var(--colour-focus-ring);
		}

		/* The Select's dropdown chevron (the trigger's last child) — the chip alone signals
		   the control. Matched at the same specificity as the status pill's chevron rules
		   above so it never reads as a descending-specificity override. */
		& > svg:last-child {
			display: none;
		}
	}

	& .owner {
		display: flex;
		gap: var(--spacing-2);
		align-items: center;
	}

	& .ownerName {
		font-size: var(--font-size-sm);
		color: var(--colour-text-secondary);
	}

	& .noOwner {
		font-size: var(--font-size-sm);
		color: var(--colour-text-tertiary);
	}

	/* Overlapping avatar stack for the distinct people on the epic's tasks. */
	& .assignees {
		display: flex;
		align-items: center;
	}

	& .avatar {
		display: inline-flex;
		margin-inline-start: -0.375rem;
		border-radius: var(--radius-full);
		box-shadow: 0 0 0 2px var(--colour-background-page);

		&:first-child {
			margin-inline-start: 0;
		}
	}

	/* Inline-editable name — reads as the page title, edits in place (no box at rest). */
	& .name {
		width: 100%;
		padding: var(--spacing-1) 0;
		margin: 0;
		font-family: inherit;
		font-size: var(--font-size-xl);
		font-weight: var(--font-weight-bold);
		line-height: var(--line-height-snug);
		color: var(--colour-text-primary);
		outline: none;
		background: transparent;
		border: none;
		border-radius: var(--radius-sm);

		&:focus-visible {
			box-shadow: 0 0 0 2px var(--colour-brand-primary);
		}
	}

	/* Inline-editable description — same in-place treatment, muted body text. */
	& .description {
		width: 100%;
		padding: var(--spacing-1) 0;
		font-family: inherit;
		font-size: var(--font-size-sm);
		line-height: var(--line-height-base);
		color: var(--colour-text-secondary);
		resize: vertical;
		outline: none;
		background: transparent;
		border: none;
		border-radius: var(--radius-sm);

		&::placeholder {
			color: var(--colour-text-tertiary);
		}

		&:focus-visible {
			box-shadow: 0 0 0 2px var(--colour-brand-primary);
		}
	}

	/* Full-width progress bar + a done/total count trailing it. */
	& .progressRow {
		display: flex;
		gap: var(--spacing-3);
		align-items: center;
		margin-block-start: var(--spacing-2);
	}

	& .progress {
		flex: 1;
		block-size: 0.5rem;
		overflow: hidden;
		background: var(--colour-background-muted);
		border-radius: var(--radius-full);
	}

	& .progressFill {
		display: block;
		block-size: 100%;
		background: var(--epic-colour);
		border-radius: var(--radius-full);
		transition: inline-size var(--motion-fast) var(--ease-standard);
	}

	& .count {
		flex: none;
		font-size: var(--font-size-xs);
		font-variant-numeric: tabular-nums;
		color: var(--colour-text-tertiary);

		& b {
			font-weight: var(--font-weight-bold);
			color: var(--colour-text-primary);
		}
	}
}
